import { Toast } from "radix-ui";
import type React from "react";
import { useEffect, useState } from "react";
import {
  MdCheckCircleOutline,
  MdErrorOutline,
  MdOutlineClose,
  MdOutlineWarningAmber,
} from "react-icons/md";
import { Heading } from "../Heading";
import { Text } from "../Text";
import styles from "./global-toast.module.scss";
import { SHOW_TOAST_EVENT } from "./ToastUtils";

export type ToastType = "success" | "warning" | "error";

export interface ToastMessage {
  title: string;
  description: string;
  type: ToastType;
  duration?: number;
}

export const GlobalToast: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const [toastDuration, setToastDuration] = useState<number>(5000);

  useEffect(() => {
    const handleShowToast = (event: CustomEvent<ToastMessage>) => {
      setMessage(event.detail);
      setToastDuration(
        event.detail.duration !== undefined ? event.detail.duration : 5000
      );
      setOpen(true);
    };

    window.addEventListener(SHOW_TOAST_EVENT, handleShowToast as EventListener);

    return () => {
      window.removeEventListener(
        SHOW_TOAST_EVENT,
        handleShowToast as EventListener
      );
    };
  }, []);

  const getIcon = () => {
    switch (message?.type) {
      case "success":
        return <MdCheckCircleOutline className={styles.messageIcon} />;
      case "warning":
        return <MdOutlineWarningAmber className={styles.messageIcon} />;
      case "error":
        return <MdErrorOutline className={styles.messageIcon} />;
      default:
        return null;
    }
  };

  return (
    <Toast.Provider
      swipeDirection="down"
      {...(toastDuration > 0 ? { duration: toastDuration } : {})}
    >
      <Toast.Root
        className={`${styles.toast} ${message ? styles[message.type] : ""}`}
        open={open}
        onOpenChange={setOpen}
      >
        {getIcon()}
        <div className={styles.textContainer}>
          <Toast.Title>
            <Heading className={styles.title} variant="2xsmall">
              {message?.title}
            </Heading>
          </Toast.Title>
          <Toast.Description>
            <Text className={styles.description} variant="xsmall">
              {message?.description}
            </Text>
          </Toast.Description>
        </div>
        <Toast.Close className={styles.action} asChild>
          <button
            type="button"
            className={styles.closeButton}
            aria-label="Close"
          >
            <MdOutlineClose />
          </button>
        </Toast.Close>
      </Toast.Root>
      <Toast.Viewport className={styles.viewport} />
    </Toast.Provider>
  );
};
